import * as React from "react";
import {
  Card,
  Subtitle1,
  Subtitle2,
  Text,
  Input,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Add20Regular, Edit20Regular, Delete20Regular, Save20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import {
  listHouseholdRoles,
  createHouseholdRole,
  updateHouseholdRole,
  deleteHouseholdRole,
  listHouseholdRelationLabels,
  createHouseholdRelationLabel,
  updateHouseholdRelationLabel,
  deleteHouseholdRelationLabel,
} from "../../services/householdListsService";
import { invalidateHouseholdListsCache } from "../../services/useHouseholdLists";
import { useBestaetigen } from "../../components/redesign/bestaetigen";

type Item = { id: number; label: string; aktiv: boolean };

/** Generische, editierbare Label-Liste (für Haushaltsrollen UND Beziehungs-Labels). */
const EditableLabelList: React.FC<{
  title: string;
  description: string;
  addLabel: string;
  load: () => Promise<Item[]>;
  create: (label: string) => Promise<unknown>;
  update: (id: number, patch: { label?: string; aktiv?: boolean }) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
}> = ({ title, description, addLabel, load, create, update, remove }) => {
  const { bestaetigen } = useBestaetigen();
  const [items, setItems] = React.useState<Item[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);
  const [addText, setAddText] = React.useState("");
  const [editId, setEditId] = React.useState<number | null>(null);
  const [editText, setEditText] = React.useState("");

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await load());
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [load]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      invalidateHouseholdListsCache();
      await reload();
      return true;
    } catch (e: any) {
      setError(String(e?.message ?? e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitAdd = async () => {
    if (!addText.trim()) {
      setError("Bitte eine Bezeichnung eingeben.");
      return;
    }
    if (await run(() => create(addText.trim()))) {
      setAddOpen(false);
      setAddText("");
    }
  };

  const submitEdit = async () => {
    if (editId == null) return;
    if (!editText.trim()) {
      setError("Bitte eine Bezeichnung eingeben.");
      return;
    }
    if (await run(() => update(editId, { label: editText.trim() }))) setEditId(null);
  };

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Subtitle1>{title}</Subtitle1>
          <Text size={200} style={{ color: "#6b5a66" }}>{description}</Text>
        </div>
        <Button appearance="primary" icon={<Add20Regular />} onClick={() => { setAddText(""); setAddOpen(true); }} disabled={busy}>
          {addLabel}
        </Button>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {addOpen && (
        <Card appearance="filled-alternative" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <Subtitle2>{addLabel}</Subtitle2>
          <Input placeholder="Bezeichnung" value={addText} onChange={(_, v) => setAddText(v.value)} style={{ minWidth: 240 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Button appearance="primary" icon={<Save20Regular />} onClick={submitAdd} disabled={busy}>Hinzufügen</Button>
            <Button icon={<Dismiss20Regular />} onClick={() => setAddOpen(false)} disabled={busy}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <Spinner label="Lade Liste…" />
      ) : items.length === 0 ? (
        <Text style={{ color: "#6b5a66" }}>Noch keine Einträge.</Text>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it) =>
            editId === it.id ? (
              <div key={it.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", flexWrap: "wrap" }}>
                <Input value={editText} onChange={(_, v) => setEditText(v.value)} style={{ minWidth: 240 }} />
                <Button size="small" appearance="primary" icon={<Save20Regular />} onClick={submitEdit} disabled={busy}>Speichern</Button>
                <Button size="small" icon={<Dismiss20Regular />} onClick={() => setEditId(null)} disabled={busy}>Abbrechen</Button>
              </div>
            ) : (
              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 4 }}>
                <span style={{ color: "#a294a0" }}>•</span>
                <Text style={{ flexGrow: 1, opacity: it.aktiv ? 1 : 0.45 }}>{it.label}</Text>
                <Button
                  size="small"
                  appearance={it.aktiv ? "primary" : "subtle"}
                  onClick={() => run(() => update(it.id, { aktiv: !it.aktiv }))}
                  disabled={busy}
                  title="Aktiv umschalten"
                >
                  {it.aktiv ? "aktiv" : "inaktiv"}
                </Button>
                <Button size="small" appearance="subtle" icon={<Edit20Regular />} title="Bearbeiten" onClick={() => { setEditId(it.id); setEditText(it.label); }} />
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  title="Löschen"
                  onClick={async () => {
                    const ok = await bestaetigen({
                      titel: "Eintrag löschen?",
                      text: `„${it.label}" wird aus der Liste entfernt.`,
                      ja: "Löschen",
                      ton: "danger",
                    });
                    if (ok) void run(() => remove(it.id));
                  }}
                />
              </div>
            ),
          )}
        </div>
      )}
    </Card>
  );
};

/** Settings-Sektion „Haushalt & Beziehungen" — zwei konfigurierbare Listen. */
export const HaushaltListsSection: React.FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    <EditableLabelList
      title="Haushaltsrollen"
      description="Rolle einer Person im Haushalt (z. B. Ehepartner, Kind). Die konkrete Beziehung (Ehefrau/Sohn …) wird aus dem Geschlecht abgeleitet — die Regeln dafür bleiben im Code."
      addLabel="Neue Rolle"
      load={async () => (await listHouseholdRoles()).map((r) => ({ id: r.id, label: r.label, aktiv: r.aktiv }))}
      create={(label) => createHouseholdRole({ label })}
      update={(id, patch) => updateHouseholdRole(id, patch)}
      remove={(id) => deleteHouseholdRole(id)}
    />
    <EditableLabelList
      title="Beziehungs-Labels"
      description="Manuell wählbare Beziehungen (z. B. Ehemann, Tochter, Onkel)."
      addLabel="Neues Label"
      load={async () => (await listHouseholdRelationLabels()).map((r) => ({ id: r.id, label: r.label, aktiv: r.aktiv }))}
      create={(label) => createHouseholdRelationLabel({ label })}
      update={(id, patch) => updateHouseholdRelationLabel(id, patch)}
      remove={(id) => deleteHouseholdRelationLabel(id)}
    />
  </div>
);
